'use client';

import { useId, type KeyboardEvent } from 'react';
import { VoiceCompanionAvatar } from '@/components/voice/voice-companion-avatar';
import type { VoiceAvatarLook } from '@/components/voice/voice-companion-face';
import type { useFloatingVoiceDockPosition } from '@/components/voice/use-floating-voice-dock-position';
import type { FloatingVoiceDockStatus } from '@/lib/floating-voice-dock-state';
import { cn } from '@/lib/utils';

type VoiceDockAvatarProps = {
  status: FloatingVoiceDockStatus;
  inputMode: 'vad' | 'ptt';
  micMuted: boolean;
  pttActive: boolean;
  /** The member's Your IMAI shape, face and colour. */
  look: VoiceAvatarLook;
  docked?: boolean;
  disabled?: boolean;
  moveHint: string;
  position: Pick<
    ReturnType<typeof useFloatingVoiceDockPosition>,
    'onDragPointerDown' | 'onDragPointerMove' | 'onDragPointerUp' | 'moveByKey'
  >;
};

export function VoiceDockAvatar(props: VoiceDockAvatarProps) {
  const moveHintId = useId();
  const avatar = (
    <VoiceCompanionAvatar
      status={props.status}
      inputMode={props.inputMode}
      micMuted={props.micMuted}
      pttActive={props.pttActive}
      look={props.look}
    />
  );
  if (props.docked) {
    return (
      <div
        data-slot="floating-voice-avatar"
        className="relative flex size-16 items-center justify-center"
      >
        {avatar}
      </div>
    );
  }
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key !== 'ArrowUp' &&
      event.key !== 'ArrowDown' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    )
      return;
    event.preventDefault();
    props.position.moveByKey(event.key);
  };
  return (
    <>
      <span id={moveHintId} className="sr-only">
        {props.moveHint}
      </span>
      <button
        type="button"
        data-slot="floating-voice-avatar"
        aria-label={props.moveHint}
        aria-describedby={moveHintId}
        title={props.moveHint}
        disabled={props.disabled}
        className={cn(
          'relative flex size-16 touch-none items-center justify-center rounded-full',
          'focus-visible:ring-primary/25 cursor-grab outline-none select-none focus-visible:ring-4 active:cursor-grabbing'
        )}
        onPointerDown={props.position.onDragPointerDown}
        onPointerMove={props.position.onDragPointerMove}
        onPointerUp={props.position.onDragPointerUp}
        onPointerCancel={props.position.onDragPointerUp}
        onKeyDown={onKeyDown}
      >
        {avatar}
      </button>
    </>
  );
}
